import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Grid,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import { Formik, Form } from 'formik';
import { useDispatch, useSelector } from 'react-redux';

import validationSchema from '../formik/edit/formModels/validationSchema';
import { shiftDemandFormModel } from '../formik/edit/formModels/shiftDemandFormModel';
import InputField from '../../../../components/formFields/InputField';
import AutocompleteField from '../../../../components/formFields/autocompleteField';
import DatePickerField from '../../../../components/formFields/datePickerField';
import { updateShiftDemandStore } from '../../../../store/shift_demands/thunks';
import { useResponseHandler } from '../../../../hooks/useResponseHandler';
import { useTableState } from '../../../../hooks/useTableState';
import { mapperShiftDemandsToBackend } from '../mappers/shiftDemands.mapper';

interface Props {
  open: boolean;
  setOpen: (open: boolean) => void;
  onSuccess: () => void;
  data?: any;
}

export const EditShiftDemandModal = ({ open, setOpen, onSuccess, data }: Props) => {
  const dispatch = useDispatch<any>();
  const { handleResponse, ResponseComponents } = useResponseHandler();
  const { pagePagination, rowsPerPage, searchTerm } = useTableState('shiftDemands');

  const cargosData = useSelector((state: any) => state.cargos?.cargos_select) || [];
  const shiftTemplatesData =
    useSelector((state: any) => state.shiftTemplates?.templates_for_select) || [];

  const [initialValues, setInitialValues] = useState<any>(null);

  const {
    formId,
    formField: { shiftTemplateId, positionId, date, requiredEmployees },
  } = shiftDemandFormModel;

  useEffect(() => {
    if (data && open) {
      setInitialValues({
        [shiftTemplateId.name]:
          shiftTemplatesData.find((item: any) => item.value === data.shiftTemplateId) || null,
        [positionId.name]: cargosData.find((item: any) => item.value === data.positionId) || null,
        [date.name]: data.date || '',
        [requiredEmployees.name]: data.requiredEmployees || '',
      });
    }
  }, [data, open, cargosData, shiftTemplatesData]);

  const handleClose = () => {
    setOpen(false);
    setInitialValues(null);
  };

  const _handleSubmit = async (values: any, actions: any) => {
    const payload = mapperShiftDemandsToBackend(values);

    const response = await dispatch(
      updateShiftDemandStore(data.id, payload, pagePagination, rowsPerPage, searchTerm, 1)
    );
    await handleResponse(response, 'actualizar requerimiento de personal', true);

    if (response?.ok) {
      handleClose();
      onSuccess();
    }
    actions.setSubmitting(false);
  };

  return (
    <>
      {ResponseComponents}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow:
              '0px 12px 24px -4px rgba(145, 158, 171, 0.12), 0px 0px 2px 0px rgba(145, 158, 171, 0.2)',
          },
        }}
      >
        <DialogTitle sx={{ m: 0, p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              p: 1,
              borderRadius: 2,
              bgcolor: 'rgba(102, 60, 193, 0.08)',
              display: 'flex',
              color: '#663cc1',
            }}
          >
            <EditIcon />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Editar Requerimiento
            </Typography>
            <Typography variant="body2" sx={{ color: '#32475C99' }}>
              Modifica los requerimientos de este turno.
            </Typography>
          </Box>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 16,
              top: 24,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        {initialValues && (
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema[0]}
            onSubmit={_handleSubmit}
            enableReinitialize
          >
            {({ isSubmitting, isValid, dirty }) => (
              <Form id={formId}>
                <DialogContent
                  dividers
                  sx={{
                    borderTop: '1px solid #32475C1F',
                    borderBottom: '1px solid #32475C1F',
                    p: 3,
                  }}
                >
                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12 }}>
                      <AutocompleteField
                        name={shiftTemplateId.name}
                        label={shiftTemplateId.label}
                        options={shiftTemplatesData}
                        fullWidth
                      />
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                      <AutocompleteField
                        name={positionId.name}
                        label={positionId.label}
                        options={cargosData}
                        fullWidth
                      />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                      <DatePickerField name={date.name} label={date.label} />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                      <InputField
                        name={requiredEmployees.name}
                        label={requiredEmployees.label}
                        type="number"
                        fullWidth
                      />
                    </Grid>
                  </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                  <Button
                    onClick={handleClose}
                    variant="text"
                    color="inherit"
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                  >
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={isSubmitting || !(isValid && dirty)}
                    startIcon={<SaveIcon />}
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                  >
                    Actualizar
                  </Button>
                </DialogActions>
              </Form>
            )}
          </Formik>
        )}
      </Dialog>
    </>
  );
};
